import {useState} from 'react';
import {
  Avatar,
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  DropZone,
  FormLayout,
  InlineStack,
  Modal,
  Page,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris';
import {ImageIcon} from '@shopify/polaris-icons';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';
import {useUnsavedChanges} from '@slj/ui/unsaved-changes';
import {NoticeToast} from '@slj/ui/toast-notice';

import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

/**
 * The self-service account surface: /settings (profile) + /settings/security.
 * Every form posts natively to its own route and redirects back with ?ok= / ?error=
 * (post-redirect-get, so a refresh never resubmits). Editable forms save through the
 * Frame's ContextualSaveBar (see useUnsavedChanges); the bar's Save just calls
 * requestSubmit() on the same native form, so the server contract is unchanged.
 * Success comes back as a Toast; errors stay as persistent Banners.
 */


function ErrorNotice({error}: {error?: string}) {
  return error ? (
    <Banner tone="critical">
      <p>{error}</p>
    </Banner>
  ) : null;
}

/**
 * Avatar uploader — Polaris DropZone (drag or click) beside the live avatar.
 *
 * The `/settings/avatar` endpoint is unchanged (multipart POST → process → PRG
 * redirect); we submit the picked file with `fetch` and follow the redirect the
 * server returns (`res.url` carries the `?ok=`/`?error=`), because a DropZone holds
 * its file in JS, not in a native input. Everything else on /settings stays native.
 */
function AvatarUploader({name, avatarUrl}: {name: string; avatarUrl: string | null}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('avatar', file);
      const res = await fetch('/settings/avatar', {method: 'POST', body});
      window.location.assign(res.url || '/settings?ok=avatar');
    } catch {
      setBusy(false);
      setError('Upload failed — check your connection and try again.');
    }
  };

  return (
    <InlineStack gap="500" blockAlign="center" wrap>
      <Avatar
        size="xl"
        name={name}
        source={avatarUrl ?? undefined}
        initials={name.slice(0, 1).toUpperCase()}
      />
      <Box minWidth="20rem">
        <BlockStack gap="200">
          <DropZone
            accept="image/jpeg,image/png,image/webp"
            type="image"
            allowMultiple={false}
            disabled={busy}
            onDrop={(_files, accepted) => {
              setError(null);
              if (accepted[0]) setFile(accepted[0]);
            }}
          >
            {file ? (
              <Box padding="400">
                <InlineStack gap="300" blockAlign="center" wrap={false}>
                  <Thumbnail size="small" alt={file.name} source={ImageIcon} />
                  <Text as="span" variant="bodyMd" truncate>
                    {file.name}
                  </Text>
                </InlineStack>
              </Box>
            ) : (
              <DropZone.FileUpload actionTitle="Add image" actionHint="or drag and drop" />
            )}
          </DropZone>
          {error ? (
            <Text as="span" tone="critical" variant="bodySm">
              {error}
            </Text>
          ) : null}
          <InlineStack gap="200">
            <Button variant="primary" onClick={upload} loading={busy} disabled={!file}>
              Upload
            </Button>
            {file ? (
              <Button onClick={() => setFile(null)} disabled={busy}>
                Remove
              </Button>
            ) : null}
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            jpeg/png/webp, up to 5 MB — cropped to a 256px square.
          </Text>
        </BlockStack>
      </Box>
    </InlineStack>
  );
}

export interface SettingsProps extends PagePropsBase {
  chrome: ChromeProps;
  ok?: string;
  error?: string;
}

export function SettingsPage({chrome, ok, error}: SettingsProps) {
  const {user} = chrome;
  const {forms, saveBar} = useUnsavedChanges({
    profile: {name: user.name},
    email: {newEmail: ''},
  });

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page title="Settings">
        <BlockStack gap="500">
          <NoticeToast notice={ok} />
          <ErrorNotice error={error} />

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Profile
              </Text>
              <Text as="p" tone="subdued">
                How you appear across apps on slj.app.
              </Text>

              <AvatarUploader name={user.name} avatarUrl={user.avatarUrl} />

              <form ref={forms.profile.formRef} method="post" action="/settings/profile">
                <FormLayout>
                  <TextField
                    label="Display name"
                    type="text"
                    id="name"
                    name="name"
                    autoComplete="name"
                    value={forms.profile.values.name}
                    onChange={(v) => forms.profile.set('name', v)}
                    maxLength={80}
                    requiredIndicator
                  />
                </FormLayout>
              </form>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Email
              </Text>
              <Text as="p" tone="subdued">
                Signed in as <code>{user.email}</code>. Changing it sends an approval link to this
                current address first — nothing moves until you follow it.
              </Text>
              <form ref={forms.email.formRef} method="post" action="/settings/email">
                <FormLayout>
                  <TextField
                    label="New email"
                    type="email"
                    id="newEmail"
                    name="newEmail"
                    autoComplete="email"
                    value={forms.email.values.newEmail}
                    onChange={(v) => forms.email.set('newEmail', v)}
                    requiredIndicator
                  />
                </FormLayout>
              </form>
            </BlockStack>
          </Card>
        </BlockStack>
        {saveBar}
      </Page>
    </AppFrame>
  );
}

export interface SessionRow {
  token: string;
  current: boolean;
  createdAt: string;
  userAgent: string | null;
  ipAddress: string | null;
}

export interface SecurityProps extends PagePropsBase {
  chrome: ChromeProps;
  sessions: SessionRow[];
  ok?: string;
  error?: string;
}

export function SecurityPage({chrome, sessions, ok, error}: SecurityProps) {
  const {user} = chrome;
  const {forms, saveBar} = useUnsavedChanges({
    password: {currentPassword: '', newPassword: '', revokeOthers: true},
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page title="Security">
        <BlockStack gap="500">
          <NoticeToast notice={ok} />
          <ErrorNotice error={error} />

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Change password
              </Text>
              <Text as="p" tone="subdued">
                Minimum 12 characters. Optionally sign out everywhere else.
              </Text>
              <form ref={forms.password.formRef} method="post" action="/settings/password">
                <FormLayout>
                  <TextField
                    label="Current password"
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    autoComplete="current-password"
                    value={forms.password.values.currentPassword}
                    onChange={(v) => forms.password.set('currentPassword', v)}
                    requiredIndicator
                  />
                  <TextField
                    label="New password"
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    autoComplete="new-password"
                    minLength={12}
                    value={forms.password.values.newPassword}
                    onChange={(v) => forms.password.set('newPassword', v)}
                    requiredIndicator
                  />
                  <Checkbox
                    label="Sign out my other sessions"
                    name="revokeOthers"
                    value="1"
                    checked={forms.password.values.revokeOthers}
                    onChange={(v) => forms.password.set('revokeOthers', v)}
                  />
                </FormLayout>
              </form>
            </BlockStack>
          </Card>

          <Card padding="0">
            <Box padding="400" paddingBlockEnd="0">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  Sessions
                </Text>
                <Text as="p" tone="subdued">
                  Everywhere this account is currently signed in.
                </Text>
              </BlockStack>
            </Box>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={['Started', 'Client', 'IP', '']}
              rows={sessions.map((s) => [
                s.createdAt,
                <span key="c" style={{display: 'inline-block', maxWidth: '22rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'}}>
                  {s.userAgent ?? '—'}{' '}
                  {s.current ? <Badge tone="success">this session</Badge> : null}
                </span>,
                <code key="ip">{s.ipAddress ?? '—'}</code>,
                s.current ? null : (
                  <form key="f" method="post" action="/settings/sessions/revoke">
                    <input type="hidden" name="token" value={s.token} />
                    <Button submit size="slim">
                      Revoke
                    </Button>
                  </form>
                ),
              ])}
            />
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Delete account
              </Text>
              <Text as="p" tone="subdued">
                Permanent. Your data in each app is removed or orphaned per that app&rsquo;s policy;
                the security audit trail is retained.
              </Text>
              <InlineStack>
                <Button tone="critical" onClick={() => setDeleteOpen(true)}>
                  Delete my account
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </BlockStack>
        {saveBar}
      </Page>

      {/* The form lives INSIDE the modal content so the native POST survives the
          portal (Modal renders outside the page's DOM tree). */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete your account?">
        <Modal.Section>
          <form method="post" action="/settings/delete">
            <BlockStack gap="400">
              <Text as="p">
                This cannot be undone. Confirm with your password.
              </Text>
              <TextField
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={deletePassword}
                onChange={setDeletePassword}
                requiredIndicator
              />
              <InlineStack align="end" gap="200">
                <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                <Button submit tone="critical" variant="primary">
                  Delete my account
                </Button>
              </InlineStack>
            </BlockStack>
          </form>
        </Modal.Section>
      </Modal>
    </AppFrame>
  );
}
